import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import WeatherScene from "@/components/v2/WeatherScene";
import { vibrate, HAPTICS } from "@/lib/haptics";

/**
 * RainConfidenceBar — horizontal-bar replacement for the circular ring.
 *
 * Layout (3 rows, never truncates):
 *   Row 1: weather icon · big score + %                | trend chip
 *   Row 2: friendly label ("Moderate chance of rain")  (full width)
 *   Row 3: gradient progress bar
 *
 * Tap → opens the WhyBottomSheet (parent passes onTap).
 */

// Plain-English copy. We embed "of rain" in the label itself so the bar
// is self-explanatory with no need for a technical caption underneath.
const LABEL_COPY = {
  none: "No rain expected",
  minimal: "Very low chance of rain",
  low: "Low chance of rain",
  moderate: "Moderate chance of rain",
  high: "High chance of rain",
  watch: "Storm watch — atmosphere loaded",
  watch_severe: "Severe storm watch",
  // Active-rain status (Phase 47). The score is a forecast probability;
  // when the live observation says rain is falling NOW, we switch the bar
  // from forecast-tense to status-tense so the label doesn't contradict
  // the headline "Heavy rain happening right now".
  active_rain: "Currently raining",
};

function scoreColor(score, label) {
  if (label === "active_rain") return "#0ea5e9"; // sky-500 — wet/active
  if (label === "watch_severe") return "#8b5cf6"; // violet
  if (label === "watch") return "#6366f1"; // indigo
  if (score == null) return "#94a3b8";
  if (score < 15) return "#10b981";
  if (score < 30) return "#84cc16";
  if (score < 50) return "#f59e0b";
  if (score < 75) return "#f97316";
  return "#fb7185";
}

export default function RainConfidenceBar({
  confidence,
  theme,
  sceneTheme,
  onTap,
  trend,
}) {
  const score = confidence?.score ?? null;
  const label = confidence?.label;
  const stroke = scoreColor(score, label);
  const [displayed, setDisplayed] = useState(0);

  // Animated count-up + haptic landing.
  useEffect(() => {
    if (score == null) return;
    let raf;
    const start = performance.now();
    const from = Math.max(0, Math.min(100, displayed));
    const to = Math.max(0, Math.min(100, score));
    const duration = 800;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (to - from) * eased);
      setDisplayed(Math.max(0, Math.min(100, v)));
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        vibrate(HAPTICS.SCORE_LAND);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [score]);

  const widthPct = score == null ? 0 : Math.max(2, Math.min(100, displayed));
  const labelText = label ? LABEL_COPY[label] || "Rain outlook" : "Loading…";

  return (
    <button
      type="button"
      onClick={onTap}
      data-testid="rain-confidence-bar"
      className="relative w-full rounded-3xl px-4 py-3.5 transition-transform active:scale-[0.985]"
      style={{
        background: theme.surface,
        border: `1px solid ${theme.surfaceBorder}`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: `0 4px 14px ${stroke}15`,
      }}
    >
      {/* Row 1 — score row: icon + big % on the left, trend chip on the right */}
      <div className="flex items-center gap-3 w-full">
        <div className="shrink-0">
          <WeatherScene theme={sceneTheme || theme} size={44} />
        </div>

        <div className="flex items-baseline gap-0.5 min-w-0 flex-1">
          <span
            className="font-display tabular-nums leading-none"
            style={{
              color: stroke,
              fontSize: 38,
              fontWeight: 700,
              letterSpacing: "-0.04em",
            }}
            data-testid="rain-confidence-score"
          >
            {score == null ? "—" : displayed}
          </span>
          <span
            className="font-medium leading-none"
            style={{ color: `${stroke}aa`, fontSize: 16, marginLeft: 2 }}
          >
            %
          </span>
        </div>

        {trend?.direction && (
          <span
            className="shrink-0 inline-flex items-center gap-0.5 px-2 py-[3px] rounded-full"
            style={{
              background: theme.chip,
              border: `1px solid ${theme.surfaceBorder}`,
              color:
                trend.direction === "up"
                  ? "#fb7185"
                  : trend.direction === "down"
                    ? "#10b981"
                    : theme.textMuted,
            }}
            data-testid="confidence-trend"
          >
            {trend.direction === "up" ? (
              <TrendUp size={10} weight="bold" />
            ) : trend.direction === "down" ? (
              <TrendDown size={10} weight="bold" />
            ) : (
              <Minus size={10} weight="bold" />
            )}
            <span className="text-[9px] font-bold tabular-nums tracking-wide">
              {trend.direction === "flat"
                ? "steady"
                : `${trend.delta > 0 ? "+" : ""}${trend.delta}pt`}
            </span>
          </span>
        )}
      </div>

      {/* Row 2 — friendly label on its own line. Full width = never truncates. */}
      <div
        className="mt-1.5 text-[13px] font-semibold leading-tight text-left"
        style={{ color: stroke, letterSpacing: "-0.005em" }}
        data-testid="rain-confidence-label"
      >
        {labelText}
      </div>

      {/* Row 3 — gradient progress bar */}
      <div
        className="mt-2.5 h-2 rounded-full overflow-hidden"
        style={{ background: `${stroke}20` }}
      >
        <motion.div
          className="h-full rounded-full"
          style={{
            background: `linear-gradient(90deg, ${stroke}cc 0%, ${stroke} 100%)`,
            boxShadow: `0 0 10px ${stroke}77`,
          }}
          initial={{ width: 0 }}
          animate={{ width: `${widthPct}%` }}
          transition={{ duration: 0.85, ease: "easeOut" }}
        />
      </div>

      {/* Row 4 — 24h confidence sparkline (renders only when we have ≥2 samples).
          Shows how the rain probability evolved through the day so the user
          can see momentum at a glance, not just the current snapshot. */}
      {trend?.history && trend.history.length >= 2 && (
        <ConfidenceSparkline
          history={trend.history}
          stroke={stroke}
          mutedColor={theme.textMuted}
        />
      )}
    </button>
  );
}

/**
 * ConfidenceSparkline — 24h SVG line of past confidence scores.
 *
 * Time axis is fixed to the last 24h so partial-day samples render at their
 * correct horizontal position (early visit + an hour later → 2 dots near the
 * right, not stretched edge-to-edge). Y-axis is the 0-100 score.
 */
function ConfidenceSparkline({ history, stroke, mutedColor }) {
  const W = 220;
  const H = 28;
  const PAD_X = 2;
  const PAD_Y = 3;
  const SPAN_MS = 24 * 60 * 60 * 1000;

  const now = Date.now();
  const start = now - SPAN_MS;

  const points = history
    .filter((p) => p.ts >= start)
    .map((p) => {
      const x =
        PAD_X +
        ((p.ts - start) / SPAN_MS) * (W - 2 * PAD_X);
      const y =
        H - PAD_Y - (Math.max(0, Math.min(100, p.score)) / 100) * (H - 2 * PAD_Y);
      return { x, y, score: p.score };
    });

  if (points.length < 2) return null;

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const last = points[points.length - 1];

  // Build a soft area fill under the line for visual weight.
  const areaPath = `${linePath} L ${last.x.toFixed(1)} ${H - PAD_Y} L ${points[0].x.toFixed(1)} ${H - PAD_Y} Z`;

  return (
    <div
      className="mt-2 -mb-0.5 flex items-center gap-1.5"
      data-testid="confidence-sparkline"
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="flex-1 h-7 w-full"
        aria-hidden
      >
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#spark-fill)" />
        <path
          d={linePath}
          fill="none"
          stroke={stroke}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.95"
        />
        {/* Trailing dot on the most recent sample */}
        <circle cx={last.x} cy={last.y} r="2.4" fill={stroke} />
        <circle
          cx={last.x}
          cy={last.y}
          r="4"
          fill="none"
          stroke={stroke}
          strokeWidth="0.9"
          opacity="0.4"
        />
      </svg>
      <span
        className="shrink-0 text-[9px] uppercase tracking-[0.14em] font-medium"
        style={{ color: mutedColor }}
      >
        24h
      </span>
    </div>
  );
}
